import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { StatCard } from '../../components/StatCard';
import { Badge } from '../../components/Badge';
import { Modal } from '../../components/Modal';
import {
  Building2,
  Users,
  Monitor,
  KeyRound,
  CheckCircle2,
  ShieldAlert,
  AlertOctagon,
  RefreshCw,
} from 'lucide-react';

export function SuperAdminDashboard() {
  const [telemetry, setTelemetry] = useState<any>(null);
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Suspend modal state
  const [suspendModalOpen, setSuspendModalOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<any>(null);
  const [suspendReason, setSuspendReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchData = async () => {
    try {
      const [telData, orgData] = await Promise.all([
        api.superadmin.getTelemetry(),
        api.superadmin.getOrganizations(),
      ]);
      setTelemetry(telData);
      setOrganizations(orgData.organizations);
    } catch (err) {
      console.error('Failed to load super admin telemetry:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const handleOpenSuspend = (org: any) => {
    setSelectedOrg(org);
    setSuspendReason('Administrative suspension pending review');
    setSuspendModalOpen(true);
  };

  const handleConfirmSuspend = async () => {
    if (!selectedOrg) return;
    setActionLoading(true);
    try {
      await api.superadmin.suspendOrg(selectedOrg.id, suspendReason);
      setSuspendModalOpen(false);
      await fetchData();
    } catch (err: any) {
      alert(err.message || 'Failed to suspend organization');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async (org: any) => {
    if (!confirm(`Reactivate organization "${org.name}"?`)) return;
    try {
      await api.superadmin.reactivateOrg(org.id);
      await fetchData();
    } catch (err: any) {
      alert(err.message || 'Failed to reactivate organization');
    }
  };

  if (loading) {
    return (
      <div className="p-8 space-y-6">
        <div className="h-8 w-48 skeleton rounded-lg" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((n) => (
            <div key={n} className="h-28 skeleton rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold text-white tracking-tight">Platform Overview</h1>
            <span className="px-2 py-0.5 text-[10px] uppercase font-bold tracking-wider rounded bg-pulse-blue/10 text-pulse-blue border border-pulse-blue/30">
              Super Admin
            </span>
          </div>
          <p className="text-xs text-zinc-400 mt-1">
            Global telemetry, tenant health, and platform governance across CK Ecosystem
          </p>
        </div>

        <button
          onClick={handleRefresh}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg bg-zinc-900 hover:bg-zinc-800 text-zinc-300 border border-zinc-800 transition-colors"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          <span>Refresh Metrics</span>
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Organizations"
          value={telemetry?.organizations?.total ?? 0}
          subtitle={`${telemetry?.organizations?.active ?? 0} Active • ${telemetry?.organizations?.suspended ?? 0} Suspended`}
          icon={<Building2 className="w-5 h-5" />}
        />
        <StatCard
          title="Monitored Users"
          value={telemetry?.users?.total ?? 0}
          subtitle="Across all customer organizations"
          icon={<Users className="w-5 h-5" />}
        />
        <StatCard
          title="Enrolled Workstations"
          value={telemetry?.devices?.total ?? 0}
          subtitle={`${telemetry?.devices?.online ?? 0} Online right now`}
          icon={<Monitor className="w-5 h-5" />}
        />
        <StatCard
          title="Active Licenses"
          value={telemetry?.licenses?.active ?? 0}
          subtitle={`${telemetry?.licenses?.unused ?? 0} Ready to activate`}
          icon={<KeyRound className="w-5 h-5" />}
        />
      </div>

      {/* Organizations Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-white tracking-tight">Customer Organizations</h2>
            <p className="text-xs text-zinc-400">Manage tenant isolation and platform access status</p>
          </div>
        </div>

        <div className="pulse-card rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-[#161616] text-zinc-400 uppercase tracking-wider font-medium border-b border-[#222222]">
                <tr>
                  <th className="py-3 px-4">Organization</th>
                  <th className="py-3 px-4">Admin Email</th>
                  <th className="py-3 px-4">Plan & Subscription</th>
                  <th className="py-3 px-4 text-center">Users</th>
                  <th className="py-3 px-4 text-center">Workstations</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1D1D1D] text-zinc-300">
                {organizations.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-12 text-center text-zinc-500">
                      <Building2 className="w-8 h-8 text-zinc-600 mx-auto mb-2 opacity-50" />
                      <p className="text-xs text-zinc-300 font-medium">No Customer Organizations Registered Yet</p>
                      <p className="text-[11px] text-zinc-500 mt-1 max-w-sm mx-auto">
                        Issue a license key to onboard customer organizations. Once activated by an admin, the tenant will appear here.
                      </p>
                      <Link
                        to="/superadmin/licenses"
                        className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 text-xs font-medium rounded-lg bg-pulse-blue hover:bg-pulse-blueHover text-white transition-colors"
                      >
                        <KeyRound className="w-3.5 h-3.5" />
                        <span>Manage & Generate Licenses</span>
                      </Link>
                    </td>
                  </tr>
                ) : (
                  organizations.map((org) => {
                    const maxDev = org.maxDevices !== undefined ? Number(org.maxDevices) : 50;
                    const isUnlimited = maxDev >= 9999;
                    const isAtLimit = !isUnlimited && org.deviceCount >= maxDev;
                    const isNearLimit = !isUnlimited && org.deviceCount >= maxDev * 0.8;

                    return (
                      <tr key={org.id} className="hover:bg-zinc-900/40 transition-colors">
                        <td className="py-3.5 px-4 font-medium text-white">
                          <div>{org.name}</div>
                          <div className="text-[11px] text-zinc-500 font-mono">{org.slug}</div>
                        </td>
                        <td className="py-3.5 px-4 text-zinc-300">{org.adminEmail}</td>
                        <td className="py-3.5 px-4">
                          <div className="space-y-1">
                            <div className="flex items-center gap-1.5">
                              <span className={`px-1.5 py-0.5 text-[9px] font-bold uppercase rounded border ${
                                org.plan === 'TRIAL'
                                  ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                                  : org.plan === 'STARTER'
                                  ? 'bg-blue-500/10 text-blue-400 border-blue-500/20'
                                  : org.plan === 'PRO'
                                  ? 'bg-pulse-blue/15 text-pulse-blue border-pulse-blue/30'
                                  : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                              }`}>
                                {org.plan || (org.subscription?.duration === 'SEVEN_DAYS' ? 'TRIAL' : 'PRO')}
                              </span>
                              <span className="font-medium text-zinc-200">
                                {org.subscription ? org.subscription.duration.replace('_', ' ') : 'N/A'}
                              </span>
                            </div>
                            <div className="text-[10px] text-zinc-500">
                              {org.subscription?.isLifetime
                                ? 'Lifetime'
                                : org.subscription?.expiresAt
                                ? `Expires ${new Date(org.subscription.expiresAt).toLocaleDateString()}`
                                : 'Active'}
                            </div>
                          </div>
                        </td>
                        <td className="py-3.5 px-4 text-center font-mono">{org.userCount}</td>
                        <td className="py-3.5 px-4 text-center">
                          <div className="inline-flex flex-col items-center">
                            <div className="flex items-center gap-1 font-mono text-xs font-semibold">
                              <span className={isAtLimit ? 'text-rose-400' : isNearLimit ? 'text-amber-400' : 'text-white'}>
                                {org.deviceCount}
                              </span>
                              <span className="text-zinc-500">/</span>
                              <span className="text-zinc-400">
                                {isUnlimited ? '∞' : maxDev}
                              </span>
                              <span className="text-[10px] text-zinc-500 font-normal">PCs</span>
                            </div>
                            {!isUnlimited && (
                              <div className="w-16 h-1 bg-zinc-800 rounded-full mt-1 overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${
                                    isAtLimit ? 'bg-rose-500' : isNearLimit ? 'bg-amber-500' : 'bg-pulse-blue'
                                  }`}
                                  style={{ width: `${Math.min(100, (org.deviceCount / maxDev) * 100)}%` }}
                                />
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-3.5 px-4">
                          <Badge status={org.status} />
                        </td>
                        <td className="py-3.5 px-4 text-right">
                          {org.status === 'ACTIVE' ? (
                            <button
                              onClick={() => handleOpenSuspend(org)}
                              className="px-2.5 py-1 text-xs rounded-lg text-rose-400 hover:bg-rose-950/40 border border-transparent hover:border-rose-800/50 transition-colors"
                            >
                              Suspend
                            </button>
                          ) : (
                            <button
                              onClick={() => handleReactivate(org.id)}
                              className="px-2.5 py-1 text-xs rounded-lg text-emerald-400 hover:bg-emerald-950/40 border border-transparent hover:border-emerald-800/50 transition-colors"
                            >
                              Reactivate
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Suspend Organization Modal */}
      <Modal
        isOpen={suspendModalOpen}
        onClose={() => setSuspendModalOpen(false)}
        title={`Suspend ${selectedOrg?.name}`}
        description="Suspended organizations and their users will immediately be blocked from accessing CK Pulse."
      >
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-zinc-300 mb-1.5">
              Suspension Reason (Visible to Customer Admin)
            </label>
            <textarea
              rows={3}
              value={suspendReason}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setSuspendReason(e.target.value)}
              className="w-full p-2.5 text-xs rounded-xl pulse-input focus:outline-none"
              placeholder="e.g. Account suspended due to policy or payment verification"
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#222222]">
            <button
              onClick={() => setSuspendModalOpen(false)}
              className="px-3 py-2 text-xs font-medium rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmSuspend}
              disabled={actionLoading || !suspendReason.trim()}
              className="px-4 py-2 text-xs font-medium rounded-lg bg-rose-600 hover:bg-rose-500 text-white transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <AlertOctagon className="w-3.5 h-3.5" />
              <span>{actionLoading ? 'Suspending...' : 'Confirm Suspension'}</span>
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
